import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { EyeOff } from "lucide-react";
import { logFounderAction } from "@/lib/founder";

const KEY = "moveload.impersonation";

export interface ImpersonationState { userId: string; label: string; startedAt: number; reason: string; }

export function getImpersonation(): ImpersonationState | null {
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as ImpersonationState; } catch { return null; }
}

export async function startImpersonation(s: Omit<ImpersonationState, "startedAt">) {
  const state: ImpersonationState = { ...s, startedAt: Date.now() };
  sessionStorage.setItem(KEY, JSON.stringify(state));
  await logFounderAction({ action: "impersonation.start", entity_type: "user", entity_id: s.userId, reason: s.reason });
  window.dispatchEvent(new Event("moveload:impersonation"));
}

export async function stopImpersonation() {
  const cur = getImpersonation();
  sessionStorage.removeItem(KEY);
  if (cur) await logFounderAction({ action: "impersonation.stop", entity_type: "user", entity_id: cur.userId, reason: `Ended session started ${new Date(cur.startedAt).toISOString()}` });
  window.dispatchEvent(new Event("moveload:impersonation"));
}

export function ImpersonationBanner() {
  const [state, setState] = useState<ImpersonationState | null>(getImpersonation());
  useEffect(() => {
    const h = () => setState(getImpersonation());
    window.addEventListener("moveload:impersonation", h);
    window.addEventListener("storage", h);
    return () => { window.removeEventListener("moveload:impersonation", h); window.removeEventListener("storage", h); };
  }, []);
  if (!state) return null;
  return (
    <div className="sticky top-0 z-50 bg-destructive text-destructive-foreground border-b border-destructive-foreground/20">
      <div className="container flex items-center justify-between gap-3 py-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <EyeOff className="h-4 w-4 shrink-0" />
          <span className="truncate">
            <strong>Impersonating {state.label}</strong> — read-only. All actions attributed to your founder ID.
          </span>
        </div>
        <Button size="sm" variant="secondary" onClick={() => stopImpersonation()}>End session</Button>
      </div>
    </div>
  );
}
